import { Fragment, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";
import { IManufacturerPriceSetting } from "../../../models/PriceSettings";

const colHelper = createColumnHelper<IManufacturerPriceSetting>();

const bandBound = (val: number | null, fallback: string) =>
    val === null || val === undefined ? fallback : `£${val}`;

const cols = [
    colHelper.accessor("make", {
        header: "Make",
    }),
    colHelper.accessor("value", {
        header: "Trade Poor From",
        cell: (info) => bandBound(info.getValue(), "No minimum"),
    }),
    colHelper.accessor("toValue", {
        header: "Trade Poor To",
        cell: (info) => bandBound(info.getValue(), "No maximum"),
    }),
    colHelper.accessor("year", {
        header: "Minimum Year",
    }),
    colHelper.display({
        id: "price",
        header: "Deduction",
        sortingFn: "auto",
        cell: (info) => (
          <span className="flex" >
            <span className="ml-4 m-auto">
                {info.row.original.priceSetting?.isFixed
                ? `£${info.row.original.priceSetting?.value}`
                : `${info.row.original.priceSetting?.value}%`
                }
            </span>
          </span>
        )
    })
];

export default function ManufacturerPriceTable({ manufacturerPriceSettings, setManufacturerPriceSetting } : { manufacturerPriceSettings: IManufacturerPriceSetting[], setManufacturerPriceSetting: any }) {
    // Group each make's bands together and list them low to high, so a ladder of bands
    // reads in order rather than in whatever order they were created.
    const [sorting, setSorting] = useState<SortingState>([
        { id: "make", desc: false },
        { id: "value", desc: false },
    ]);

    const table = useReactTable({
        data: manufacturerPriceSettings,
        columns: cols,
        state: {
            sorting: sorting,
        },
        enableRowSelection: true,
        getExpandedRowModel: getExpandedRowModel(),
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <>
            <div className="mt-4 bg-white overflow-auto">
                <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
        
                {/* Table headings - very similar to all the other tables */}
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="bg-gp-blue-50 text-gray-800">
                        {headerGroup.headers.map((header, i) => (
                        <th
                            key={header.id}
                            scope="col"
                            className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                        >
                            {header.isPlaceholder
                            ? null
                            : (
                                <div
                                {...{
                                    className: header.column.getCanSort()
                                    ? 'cursor-pointer select-none'
                                    : '',
                                    onClick: header.column.getToggleSortingHandler(),
                                }}
                                >
                                {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                )}
                                {{
                                    asc: ' ▲',
                                    desc: ' ▼',
                                }[header.column.getIsSorted() as string] ?? null}
                                </div>
        
                            )}
                        </th>
                        ))}
                    </tr>
                    ))}
                </thead>
                
                {/* Body */}
                <tbody>
                {table.getRowModel().rows.map((row, i) => {
                    let rowCells = row.getVisibleCells();
                    return (
                        <Fragment key={i}>
                        <tr
                            key={row.id}
                            onClick={() => setManufacturerPriceSetting(row.original)}
                            className={"hover:bg-gray-100 hover:cursor-pointer"}
                        >
                            {rowCells.map((cell) => (
                            <td key={cell.id} className="px-3 py-3 border text-sm">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                            ))}
                        </tr>
                        {row.getIsExpanded() && (
                            <></>
                            // <tr>
                            //   {/* 2nd row is a custom 1 cell row */}
                            //   <td colSpan={row.getVisibleCells().length}>
                            //     <LeadsTableSubComponent lead={row.original} />
                            //   </td>
                            // </tr>
                        )}
                        </Fragment>
                    );
                    })}
                </tbody>
        
                </table>
            </div>
        </>
    );
}